"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight } from "lucide-react";
const steps = [{
  id: 1,
  title: "Understand Your Goals",
  description: "We begin with a comprehensive assessment of your financial position and long-term objectives to determine your borrowing power."
}, {
  id: 2,
  title: "Compare Lenders",
  description: "Our team analyses options from over 50+ leading lenders, comparing interest rates and loan features to find your perfect match."
}, {
  id: 3,
  title: "Secure Best Option",
  description: "We handle the heavy lifting, managing your application from submission to approval while negotiating the most favorable terms."
}, {
  id: 4,
  title: "Ongoing Support",
  description: "Our relationship continues beyond settlement with annual reviews and proactive advice as your financial needs evolve."
}];
export default function TheRoadToClosing() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223476" id="the-lending-journey" className="bg-background py-24 px-6 md:px-12 overflow-hidden" ref={ref}>
      <div className={`max-w-6xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-primary mb-6 tracking-tight">
            The Lending Journey
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
            A transparent, professional approach to securing your financial future with expert guidance at every milestone of the process.
          </p>
        </div>

        <div className="relative mb-20">
          {}
          <div className="absolute top-8 left-[10%] right-[10%] h-0.5 border-t-2 border-dashed border-secondary z-0 hidden md:block opacity-40" />

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-12 md:gap-4 relative z-10">
            <TooltipProvider delayDuration={200}>
              {steps.map((step, i) => <div key={step.id} data-index={i} className="flex flex-col items-center relative w-full md:w-1/4 group">
                  {}
                  {i !== steps.length - 1 && <div className="absolute top-16 left-1/2 w-0.5 h-[calc(100%+1rem)] border-l-2 border-dashed border-secondary -translate-x-1/2 z-0 md:hidden opacity-40" />}

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground border-2 border-ring flex items-center justify-center text-2xl font-bold font-default shadow-lg cursor-help z-10 relative transition-all duration-300 group-hover:scale-110 group-hover:bg-accent group-hover:text-accent-foreground group-hover:shadow-xl">
                        {step.id}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[250px] text-center p-4 shadow-2xl bg-card border-border" sideOffset={10}>
                      <p className="font-bold font-default text-base mb-2 text-foreground">
                        {step.title}
                      </p>
                      <p className="text-sm font-default opacity-90 leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <div className="mt-6 text-center bg-background/80 backdrop-blur-sm px-4 py-2 rounded-md">
                    <h3 className="font-default font-semibold text-xl text-foreground mb-2">
                      {step.title}
                    </h3>
                    {}
                    <p className="text-sm text-muted-foreground font-default md:hidden max-w-[250px] mx-auto">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </TooltipProvider>
          </div>
        </div>

        <div className="text-center">
          <Button asChild size="lg" className="rounded-full px-10 py-6 text-base shadow-lg bg-primary hover:bg-secondary transition-colors font-default font-semibold">
            <Link to="/contact">
              Start Your Journey
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}